/**
 * RentalsDashboard — Admin view for Birthday Party Booking requests.
 * Surfaces key booking details inline so admins can triage at a glance.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { config } from '../../config';
import { birthdayPartyBookingQuestions } from '../../survey/birthdayPartyBookingQuestions';
import type { SurveyQuestion } from '../../survey/types';
import './RentalsDashboard.css';

// ── Types ───────────────────────────────────────────────────────────────────

interface SurveyResponseItem {
  id: string;
  question_id: string;
  answer_text: string | null;
  answer_json: string | null;
}

interface Submission {
  id: string;
  survey_slug: string;
  respondent_name: string | null;
  respondent_email: string | null;
  submitted_at: string;
  responses: SurveyResponseItem[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function getAnswer(sub: Submission, questionId: string): string {
  const r = sub.responses.find((x) => x.question_id === questionId);
  return r?.answer_text || r?.answer_json || '';
}

function getQuestionDef(id: string): SurveyQuestion | undefined {
  return birthdayPartyBookingQuestions.find((q) => q.id === id);
}

function getLabel(questionId: string, value: string): string {
  const q = getQuestionDef(questionId);
  if (!q?.options) return value;
  return q.options.find((o) => o.value === value)?.label ?? value;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Vancouver',
  });
}

// Strip emoji prefix from theme labels for compact display
function shortTheme(raw: string): string {
  return raw.replace(/^[^\w]+ /, '').split(' — ')[0];
}

// ── Detail Modal ─────────────────────────────────────────────────────────────

const BookingDetail: React.FC<{
  sub: Submission;
  onClose: () => void;
  onDeleted: (id: string) => void;
}> = ({ sub, onClose, onDeleted }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      await axios.delete(
        `${config.BACKEND_URL}/api/surveys/submission/${sub.id}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      onDeleted(sub.id);
      onClose();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete booking.');
      setDeleting(false);
    }
  };

  const qOrder = birthdayPartyBookingQuestions.map((q) => q.id);
  const sorted = useMemo(() => {
    const map = new Map(sub.responses.map((r) => [r.question_id, r]));
    const out: SurveyResponseItem[] = [];
    for (const qId of qOrder) {
      const r = map.get(qId);
      if (r) out.push(r);
    }
    return out;
  }, [sub.responses, qOrder]);

  return (
    <div className="rd-modal-overlay" onClick={onClose}>
      <div className="rd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rd-modal-header">
          <div>
            <div className="rd-modal-subtitle">Birthday Party Booking</div>
            <h2 className="rd-modal-title">
              {sub.respondent_name || 'Unknown'}
            </h2>
            <div className="rd-modal-meta">
              {sub.respondent_email && <span>{sub.respondent_email} · </span>}
              <span>Submitted {formatDate(sub.submitted_at)}</span>
            </div>
          </div>
          <button className="rd-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="rd-modal-body">
          {sorted.map((r) => {
            const qDef = getQuestionDef(r.question_id);
            const label = qDef?.label || r.question_id;
            let answer = '';
            if (r.answer_json) {
              try { answer = JSON.parse(r.answer_json).join(', '); } catch { answer = r.answer_json; }
            } else if (r.answer_text) {
              answer = qDef?.options ? getLabel(r.question_id, r.answer_text) : r.answer_text;
            }
            return (
              <div key={r.id} className="rd-qa">
                <div className="rd-question">{label}</div>
                <div className="rd-answer">{answer || <em className="rd-no-answer">No answer</em>}</div>
              </div>
            );
          })}

          <div className="rd-meta-section">
            <div className="rd-meta-row"><span>Submission ID</span><code>{sub.id}</code></div>
            <div className="rd-meta-row"><span>Submitted</span><span>{formatDate(sub.submitted_at)}</span></div>
          </div>

          <div className="rd-delete-section">
            {!confirmDelete ? (
              <button className="rd-delete-btn" onClick={() => setConfirmDelete(true)}>
                🗑 Delete this booking request
              </button>
            ) : (
              <div className="rd-delete-confirm">
                <p>Permanently delete this booking request? This cannot be undone.</p>
                <div className="rd-delete-actions">
                  <button className="rd-delete-yes" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Yes, delete'}
                  </button>
                  <button className="rd-delete-no" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const RentalsDashboard: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Submission | null>(null);
  const [search, setSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const res = await axios.get(
        `${config.BACKEND_URL}/api/surveys/birthday-party-booking`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setSubmissions(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleDeleted = useCallback((id: string) => {
    setSubmissions((prev) => prev.filter((s) => s.id !== id));
    setConfirmDeleteId(null);
  }, []);

  const handleRowDelete = useCallback(async (id: string) => {
    setDeleting(true);
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      await axios.delete(
        `${config.BACKEND_URL}/api/surveys/submission/${id}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      handleDeleted(id);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete booking.');
    } finally {
      setDeleting(false);
    }
  }, [handleDeleted]);

  const filtered = useMemo(() => {
    if (!search.trim()) return submissions;
    const q = search.toLowerCase();
    return submissions.filter((s) => {
      const name  = (s.respondent_name  || '').toLowerCase();
      const email = (s.respondent_email || '').toLowerCase();
      const child = getAnswer(s, 'q_child_name').toLowerCase();
      const date  = getAnswer(s, 'q_party_date').toLowerCase();
      return name.includes(q) || email.includes(q) || child.includes(q) || date.includes(q);
    });
  }, [submissions, search]);

  return (
    <div className="rd-container">
      {/* Header */}
      <div className="rd-header">
        <div>
          <h2 className="rd-title">🎂 Birthday Party Booking Requests</h2>
          <p className="rd-subtitle">
            {loading ? 'Loading…' : `${submissions.length} request${submissions.length !== 1 ? 's' : ''} · stored in database`}
          </p>
        </div>
        <div className="rd-header-actions">
          <input
            className="rd-search"
            type="text"
            placeholder="Search by name, email, child, date…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="rd-refresh-btn" onClick={fetchBookings} disabled={loading}>
            {loading ? '…' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {/* States */}
      {loading && <div className="rd-state">Loading bookings…</div>}
      {error   && <div className="rd-state rd-state-error">{error}</div>}

      {!loading && !error && submissions.length === 0 && (
        <div className="rd-state rd-state-empty">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <p>No birthday party booking requests yet.</p>
          <p style={{ fontSize: '0.9rem', color: '#64748b' }}>
            Submissions from <a href="/birthday-party-booking" target="_blank" rel="noreferrer">/birthday-party-booking</a> will appear here.
          </p>
        </div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <div className="rd-table-wrap">
          {search && (
            <div className="rd-filter-note">
              Showing {filtered.length} of {submissions.length} · <button className="rd-clear-search" onClick={() => setSearch('')}>Clear search</button>
            </div>
          )}
          <table className="rd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Parent / Guardian</th>
                <th>Birthday Child</th>
                <th>Party Date</th>
                <th>Time Slot</th>
                <th>Group</th>
                <th>Theme</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((sub, i) => {
                const childName = getAnswer(sub, 'q_child_name');
                const childAge  = getAnswer(sub, 'q_child_age');
                const partyDate = getAnswer(sub, 'q_party_date');
                const timeSlot  = getAnswer(sub, 'q_party_time');
                const numKids   = getAnswer(sub, 'q_num_kids');
                const theme     = getAnswer(sub, 'q_theme');
                const phone     = getAnswer(sub, 'q_phone');

                const isConfirming = confirmDeleteId === sub.id;

                return (
                  <tr
                    key={sub.id}
                    className={`rd-row${isConfirming ? ' rd-row-confirm' : ''}`}
                    onClick={() => !isConfirming && setSelected(sub)}
                  >
                    <td className="rd-num">{submissions.length - i}</td>
                    <td>
                      <div className="rd-cell-main">{sub.respondent_name || <em>Unknown</em>}</div>
                      {sub.respondent_email && <div className="rd-cell-sub">{sub.respondent_email}</div>}
                      {phone && <div className="rd-cell-sub">📱 {phone}</div>}
                    </td>
                    <td>
                      <div className="rd-cell-main">{childName || '—'}</div>
                      {childAge && <div className="rd-cell-sub">Age {childAge}</div>}
                    </td>
                    <td>
                      <div className="rd-cell-main rd-date-highlight">{partyDate || '—'}</div>
                      {getAnswer(sub, 'q_alt_date') && (
                        <div className="rd-cell-sub">Alt: {getAnswer(sub, 'q_alt_date')}</div>
                      )}
                    </td>
                    <td className="rd-cell-compact">{timeSlot || '—'}</td>
                    <td className="rd-cell-compact">{numKids ? `${numKids} kids` : '—'}</td>
                    <td>
                      {theme ? (
                        <span className="rd-theme-badge">{shortTheme(getLabel('q_theme', theme))}</span>
                      ) : '—'}
                    </td>
                    <td className="rd-date">{formatDate(sub.submitted_at)}</td>
                    <td className="rd-view-cell" onClick={(e) => e.stopPropagation()}>
                      {isConfirming ? (
                        <div className="rd-row-confirm-wrap">
                          <span className="rd-row-confirm-label">Remove?</span>
                          <button
                            className="rd-row-confirm-yes"
                            onClick={() => handleRowDelete(sub.id)}
                            disabled={deleting}
                          >
                            {deleting ? '…' : 'Yes'}
                          </button>
                          <button
                            className="rd-row-confirm-no"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={deleting}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="rd-row-btns">
                          <button className="rd-view-btn" onClick={() => setSelected(sub)}>
                            View
                          </button>
                          <button
                            className="rd-row-delete-btn"
                            onClick={() => setConfirmDeleteId(sub.id)}
                            title="Remove this booking request"
                          >
                            🗑
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <BookingDetail
          sub={selected}
          onClose={() => setSelected(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default RentalsDashboard;
